import { Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { FileText, Building2, CheckCircle, Clock, AlertCircle } from "lucide-react";

interface Product {
  id: string;
  name: string;
  description?: string;
  manufacturer?: string;
  certification_status: string;
  created_at: string;
  product_type: string;
}

interface GSProductCardProps {
  product: Product;
}

export function GSProductCard({ product }: GSProductCardProps) {
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'certified':
        return <CheckCircle className="h-4 w-4 text-green-600" />;
      case 'pending':
        return <Clock className="h-4 w-4 text-yellow-600" />;
      case 'in_review':
        return <AlertCircle className="h-4 w-4 text-blue-600" />;
      default:
        return <Clock className="h-4 w-4 text-gray-400" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'certified':
        return 'bg-green-100 text-green-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'in_review':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatStatus = (status: string) => {
    return status.split('_').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  return (
    <Card className="hover:shadow-md transition-shadow">
      <CardHeader className="pb-4">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg font-medium">{product.name}</CardTitle>
          <div className="flex items-center gap-2">
            {getStatusIcon(product.certification_status)}
            <Badge className={getStatusColor(product.certification_status)}>
              {formatStatus(product.certification_status)}
            </Badge>
          </div>
        </div>
      </CardHeader>
      
      <CardContent className="space-y-4">
        {product.description && (
          <p className="text-sm text-muted-foreground">{product.description}</p>
        )}
        
        {product.manufacturer && (
          <div className="flex items-center gap-2 text-sm">
            <Building2 className="h-4 w-4 text-muted-foreground" />
            <span className="text-muted-foreground">Manufacturer:</span>
            <span>{product.manufacturer}</span>
          </div>
        )}

        <div className="flex items-center justify-between pt-2">
          <div className="text-xs text-muted-foreground">
            Added {new Date(product.created_at).toLocaleDateString()}
          </div>
          
          <Link to={`/gs-document-tool/products/${product.id}`}>
            <Button variant="outline" size="sm">
              <FileText className="h-4 w-4 mr-2" />
              {product.product_type === 'GS37' ? 'View Documentation' : 'Manage Documents'}
            </Button>
          </Link>
        </div>
      </CardContent>
    </Card>
  );
}